import { TempusDominus } from "@eonasdan/tempus-dominus";
import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "startInput",
    "finishInput",
    "kindOption",
    "finishDialy",
    "finishHourly",
  ];

  static values = {
    stepping: { type: Number, default: 30 },
  };

  connect() {
    this.handleStartChange = this.handleStartChange.bind(this);

    if (this.hasStartInputTarget) {
      this.startPicker = new TempusDominus(
        this.startInputTarget,
        this.pickerOptions({ minDateNow: true }),
      );
      this.startInputTarget.addEventListener("change", this.handleStartChange);
    }

    if (this.hasFinishInputTarget && !this.finishInputTarget.readOnly) {
      this.finishPicker = new TempusDominus(
        this.finishInputTarget,
        this.pickerOptions(),
      );
    }

    this.updateFinishRestrictions();
    this.updateKindUi();
  }

  disconnect() {
    if (this.hasStartInputTarget) {
      this.startInputTarget.removeEventListener(
        "change",
        this.handleStartChange,
      );
    }

    if (this.startPicker) {
      this.startPicker.dispose();
      this.startPicker = null;
    }

    if (this.finishPicker) {
      this.finishPicker.dispose();
      this.finishPicker = null;
    }
  }

  onKindChange() {
    this.updateKindUi();
    this.updateFinishFromKind();
  }

  handleStartChange() {
    this.updateFinishRestrictions();
    this.updateFinishFromKind();
  }

  pickerOptions({ minDateNow = false } = {}) {
    const restrictions = {};

    if (minDateNow) {
      restrictions.minDate = new Date();
    }

    return {
      useCurrent: false,
      allowInputToggle: false,
      stepping: this.steppingValue,
      localization: {
        locale: "pt-BR",
        format: "dd/MM/yyyy HH:mm",
      },
      display: {
        keepOpen: false,
        inline: false,
        icons: {
          type: "icons",
          time: "far fa-clock",
          date: "far fa-calendar-alt",
          up: "fas fa-arrow-up",
          down: "fas fa-arrow-down",
          previous: "fas fa-chevron-left",
          next: "fas fa-chevron-right",
          today: "far fa-calendar-check",
          clear: "far fa-trash-alt",
          close: "fas fa-times",
        },
        components: {
          calendar: true,
          date: true,
          month: true,
          year: true,
          decades: true,
          clock: true,
          hours: true,
          minutes: true,
          seconds: false,
          useTwentyfourHour: true,
        },
      },
      restrictions,
    };
  }

  updateFinishRestrictions() {
    if (!this.finishPicker) {
      return;
    }

    const startDate = this.startDate();

    if (!startDate) {
      this.finishPicker.updateOptions({ restrictions: {} });
      return;
    }

    this.finishPicker.updateOptions({
      restrictions: {
        minDate: startDate,
      },
    });
  }

  updateFinishFromKind() {
    if (!this.hasFinishInputTarget || this.finishInputTarget.readOnly) {
      return;
    }

    if (!this.hasKindOptionTarget) {
      return;
    }

    const startDate = this.startDate();
    if (!startDate) {
      return;
    }

    const kind = this.currentKind();
    const finish = new Date(startDate.getTime());

    if (kind === "hourly") {
      finish.setHours(finish.getHours() + 1);
    } else {
      finish.setDate(finish.getDate() + 1);
      finish.setHours(16, 20, 0, 0);
    }

    this.finishInputTarget.value = this.formatDateTime(finish);
    this.finishInputTarget.dispatchEvent(
      new Event("change", { bubbles: true }),
    );
  }

  updateKindUi() {
    if (!this.hasKindOptionTarget) {
      return;
    }

    const hourly = this.currentKind() === "hourly";

    if (this.hasFinishDialyTarget) {
      this.finishDialyTarget.style.display = hourly ? "none" : "";
    }

    if (this.hasFinishHourlyTarget) {
      this.finishHourlyTarget.style.display = hourly ? "" : "none";
    }
  }

  currentKind() {
    const checked = this.kindOptionTargets.find((option) => option.checked);
    return checked ? checked.value : "dialy";
  }

  startDate() {
    if (!this.hasStartInputTarget || !this.startInputTarget.value) {
      return null;
    }

    return this.parseDateTime(this.startInputTarget.value);
  }

  parseDateTime(value) {
    const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})\s(\d{2}):(\d{2})$/);
    if (!match) {
      return null;
    }

    const [, day, month, year, hours, minutes] = match;
    const parsed = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hours),
      Number(minutes),
      0,
      0,
    );

    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  formatDateTime(date) {
    const pad = (value) => String(value).padStart(2, "0");

    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }
}
